import { withDevtools, withImmutableState } from "@angular-architects/ngrx-toolkit";
import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withHooks, withMethods } from "@ngrx/signals";
import { catchError, finalize, map } from "rxjs";
import { ChatBotService, ResponseMessage, ResponseMessageRoleEnum } from "../../../api";

export enum AnswerStatus {
  LOADING = "loading",
  ERROR = "error",
  STREAMING = "streaming",
  DONE = "done"
}

export enum ChatStatus {
  READY = "ready",
  STREAMING = "streaming"
}

export interface Message {
  question: string;
  answer: string;
  status?: AnswerStatus;
}

export type ChatState = {
  messages: { [id: string]: Message };
  status: ChatStatus;
  conversationId: string | null;
}

const initialState: ChatState = {
  messages: {},
  status: ChatStatus.READY,
  conversationId: null,
};


export const ChatStore = signalStore(
  { providedIn: "root" },
  withImmutableState(initialState),
  withDevtools("chat"),
  withComputed(({ messages, conversationId }) => ({
    messageCount: computed(() => Object.values(messages()).length),
    messageArray: computed(() => {
      return Object.values(messages());
    }),
    requestBody: computed(() => {
        return {
          messages: Object.values(messages()).reduce((res, cur) => {
            res.push({ content: cur.question, role: ResponseMessageRoleEnum.User });
            res.push({ content: cur.answer, role: ResponseMessageRoleEnum.Assistant });
            return res;
          }, [] as ResponseMessage[]).filter(r => !!r.content),
          stream: true,
          user_id: "2",
          conversation_id: conversationId(),
        };
      },
    ),
  })),
  withMethods((store, chatBotService = inject(ChatBotService)) => ({
    async _addMessage(question: string): Promise<number> {
      const nextId = store.messageArray().length + 1;

      patchState(store, (state) => ({
        ...state,
        messages: {
          ...state.messages,
          [nextId]: {
            question,
            answer: "",
            status: AnswerStatus.LOADING,
          },
        },
      }));

      return nextId;
    },
    async _updateStatus(messageId: number, status: AnswerStatus): Promise<void> {
      patchState(store, (state) => ({
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...state.messages[messageId],
            status,
          },
        },
      }));
    },
    async _appendContent(messageId: number, content: string): Promise<void> {
      patchState(store, (state) => ({
        ...state,
        messages: {
          ...state.messages,
          [messageId]: {
            ...state.messages[messageId],
            answer: state.messages[messageId].answer + content,
          },
        },
      }));
    },
    async ask(question: string): Promise<void> {
      patchState(store, (state) => ({
        ...state,
        status: ChatStatus.STREAMING,
        conversationId: state.conversationId ?? generateRandomConversationId(),
      }));
      let messageId = await this._addMessage(question);

      chatBotService.stream(this.requestBody(), "response")
        .pipe(
          map(res => res.body as Blob),
          catchError((e) => {
            this._updateStatus(messageId, AnswerStatus.ERROR);
            patchState(store, (state) => ({ ...state, status: ChatStatus.READY }));
            throw e;
          }),
        )
        .subscribe(async (blob) => {
          await this._updateStatus(messageId, AnswerStatus.STREAMING);

          const reader = blob.stream().getReader();

          const processLine = async (line: string) => {
            if (!line.trim()) return;
            try {
              const data = JSON.parse(line);
              const contentChunk = data.choices[0].delta.content;
              if (contentChunk) {
                await this._appendContent(messageId, contentChunk);
              }
            } catch (e) {
              console.error("Error parsing line:", line, e);
            }
          };

          await readStream(reader, processLine, () => (this.status() === "streaming"));

          await this._updateStatus(messageId, AnswerStatus.DONE);
          patchState(store, (state) => ({ ...state, status: ChatStatus.READY }));
        });
    },
    async reset(): Promise<void> {
      patchState(store, initialState);
    },
    async stop(): Promise<void> {
      patchState(store, (state) => ({ ...state, status: ChatStatus.READY }));
    },
  })),
  withHooks({
    onDestroy(store) {
      console.log("state destroyed", store);
    },
  }),
);

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function readStream(reader: ReadableStreamDefaultReader<Uint8Array>, processLine: (line: string) => Promise<void>, streaming: () => boolean) {
  const decoder = new TextDecoder();
  let buffer = "";

  // TODO while chat status = 'streaming'
  while (streaming()) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });

    // Process all complete lines
    let lineEndIndex: number;
    while ((lineEndIndex = buffer.indexOf("}{")) >= 0 && streaming()) {
      await sleep(Math.random() * 50);

      const line = buffer.substring(0, lineEndIndex + 1);
      buffer = buffer.substring(lineEndIndex + 1);
      await processLine(line);
    }
  }
}

// copied from fritzbot
export const generateRandomConversationId = () => {
  // Get the current timestamp in the format YYYYMMDDHHMMSS
  const now = new Date();
  const timestamp = now.getFullYear().toString() +
    (now.getMonth() + 1).toString().padStart(2, "0") +
    now.getDate().toString().padStart(2, "0") +
    now.getHours().toString().padStart(2, "0") +
    now.getMinutes().toString().padStart(2, "0") +
    now.getSeconds().toString().padStart(2, "0");
  // Generate a 16-digit random hexadecimal number
  const randomHex = [...Array(16)].map(() => Math.floor(Math.random() * 16).toString(16)).join("");

  return timestamp + randomHex;
};
